import { useEffect, useRef, useState } from 'react'
import * as Collapsible from '@radix-ui/react-collapsible'
import { ChevronUp, ChevronDown, Trash2 } from 'lucide-react'
import { useConnection } from '@/features/connection/ConnectionContext'
import { Button } from '@/components/ui/Button'
import { clsx } from 'clsx'

interface ConsoleDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ConsoleDrawer({ open, onOpenChange }: ConsoleDrawerProps) {
  const { onMessage, isConnected } = useConnection()
  const [messages, setMessages] = useState<string[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Subscribe to serial messages
  useEffect(() => {
    if (!isConnected) return

    const unsubscribe = onMessage((message) => {
      setMessages((prev) => {
        const newMessages = [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]
        // Keep only last 500 messages to prevent memory issues
        if (newMessages.length > 500) {
          return newMessages.slice(-500)
        }
        return newMessages
      })
    })

    return unsubscribe
  }, [isConnected, onMessage])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, open])

  const clearMessages = () => {
    setMessages([])
  }

  return (
    <Collapsible.Root open={open} onOpenChange={onOpenChange}>
      <Collapsible.Trigger asChild>
        <button
          className={clsx(
            'w-full flex items-center justify-between px-4 py-2',
            'bg-zinc-900 border-t border-zinc-800',
            'text-zinc-400 hover:text-white transition-colors',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500'
          )}
        >
          <span className="text-sm font-medium flex items-center gap-2">
            Console
            {messages.length > 0 && (
              <span className="text-xs bg-zinc-800 px-2 py-0.5 rounded-full">
                {messages.length}
              </span>
            )}
          </span>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </Collapsible.Trigger>

      <Collapsible.Content>
        <div className="bg-zinc-950 border-t border-zinc-800">
          {/* Console header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800">
            <span className="text-xs text-zinc-500">Serial Output</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearMessages}
              className="text-zinc-500 hover:text-white"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Clear
            </Button>
          </div>

          {/* Console output */}
          <div className="h-48 overflow-auto p-2 font-mono text-xs">
            {messages.length === 0 ? (
              <div className="text-zinc-600 text-center py-4">
                No messages yet. Connect to start receiving data.
              </div>
            ) : (
              messages.map((msg, i) => (
                <div
                  key={i}
                  className={clsx(
                    'py-0.5',
                    msg.includes('ERROR') && 'text-red-400',
                    msg.includes('OK:') && 'text-green-400',
                    msg.includes('DEBUG') && 'text-yellow-400',
                    !msg.includes('ERROR') && !msg.includes('OK:') && !msg.includes('DEBUG') && 'text-zinc-400'
                  )}
                >
                  {msg}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  )
}
